import React, {useState, useCallback} from 'react';
import {Button, Card, Col, DatePicker, Form, message, Row, Table} from "antd";
import {useSelector} from "react-redux";
import {useNewIdleSummaryReportMutation} from "../../../slices/rootApiSlices/reportApiSlice";
import jsPDF from "jspdf";
import SelectVehicle from "./SelectVehicle";
import {DownloadOutlined, FilePdfOutlined, FileTextOutlined} from "@ant-design/icons";
import Title from "../../../components/Title";
import 'jspdf-autotable';
import * as XLSX from "xlsx";
import moment from "moment";

const ContactReport = () => {

    const [tableData, setTableData] = useState([]);
    const [infoData, setInfoData] = useState({});
    const {RangePicker} = DatePicker;
    const [endDate, setEndDate] = useState(null);
    const [startDate, setStartDate] = useState(null);
    const [vehicle_id, setVehicleID] = useState('');
    const {user} = useSelector((state) => state.auth);
    const [fuelHandle, {isLoading}, error] = useNewIdleSummaryReportMutation();

    function disabledDate(current) {
        const tomorrow = moment().add(1, 'days').startOf('day');
        return current && current >= tomorrow;
    }

    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 30,
        total: 0,
    });

    const handleQuery = useCallback(async () => {
        try {
            const res = await fuelHandle({
                id: vehicle_id,
                token: user?.token,
                startDate,
                endDate,
            }).unwrap();
            setInfoData(res?.data?.vehicleInfo);
            setTableData(res?.data?.data);
            message.success("İşlem Başarılı..");
        } catch (e) {
            message.error(
                e?.data?.messageTR ||
                "Bir hata oluştu. Lütfen bir süre sonra tekrar deneyiniz."
            );
        }
    }, [fuelHandle, startDate, endDate, user?.token, vehicle_id, pagination]);

    function handleDateChange(date, dateString) {
        const adjustDate = (dateStr) => {
            const date = new Date(dateStr.replace(' ', 'T') + '.000Z');
            date.setHours(date.getHours() - 3); // Subtract 3 hours
            return date.toISOString().slice(0, 19).replace('T', ' ');
        };

        const startDate = adjustDate(dateString[0]);
        const endDate = adjustDate(dateString[1]);

        setStartDate(startDate);
        setEndDate(endDate);
    }

    const onPageChange = (page, pageSize) => {
        setPagination({current: page, pageSize: pageSize});
    };

    const columns = [
        {
            title: 'Oluşturulma Tarihi',
            dataIndex: 'createdAt',
            key: 'createdAt',
            render: (text) => {
                const date = new Date(text);
                date.setHours(date.getHours() + 3);
                const adjustedDate = date.toISOString().slice(0, 19).replace('T', ' ');
                return <span>{adjustedDate}</span>;
            },
        },
        {
            title: 'Kapı Numarası',
            dataIndex: 'operationNumber',
            key: 'operationNumber',
        },
        {
            title: 'Plaka',
            dataIndex: 'plateNumber',
            key: 'plateNumber',
        },
        {
            title: 'Şasi Numarası',
            dataIndex: 'chassisNumber',
            key: 'chassisNumber',
        },
        {
            title: 'Başlangıç Tarihi',
            dataIndex: ['dates', 'start'],
            key: 'start',
            render: (text) => {
                const date = new Date(text);
                date.setHours(date.getHours() + 6);
                const adjustedDate = date.toISOString().slice(0, 19).replace('T', ' ');
                return <span>{adjustedDate}</span>;
            },
        },
        {
            title: 'Bitiş Tarihi',
            dataIndex: ['dates', 'end'],
            key: 'end',
            render: (text) => {
                const date = new Date(text);
                date.setHours(date.getHours() + 6);
                const adjustedDate = date.toISOString().slice(0, 19).replace('T', ' ');
                return <span>{adjustedDate}</span>;
            },
        },
        {
            title: 'Süre',
            dataIndex: 'duration',
            key: 'duration',
        },
        {
            title: 'Motor Hızı [RPM]',
            dataIndex: 'engineSpeed',
            key: 'engineSpeed',
            render: (rpm) => <span>{rpm !== null ? rpm : '-'}</span>,
        },
        {
            title: 'Araç Hızı [km/h]',
            dataIndex: 'wheelBasedSpeed',
            key: 'wheelBasedSpeed',
            render: (speed) => <span>{speed !== null ? speed : '-'}</span>,
        },
        {
            title: 'Konum',
            dataIndex: 'gps',
            key: 'gps',
            render: (gps) => (
                <span>
                {gps ? gps : '-'}
            </span>
            ),
        },
    ];

    const generatePDF = () => {
        const doc = new jsPDF('landscape', 'mm', 'a3');

        // Excel'deki Bilgiler
        const kapıNumarası = infoData?.operationNumber || '-';
        const araçPlakası = infoData?.plateNumber || '-';
        const chassisNumber = infoData?.chassisNumber || 'Tanimlanmadi';
        const toplamKM = infoData?.totalDistance || '-';
        const raporTarihAralıkları = startDate && endDate
            ? `${moment(startDate).add(3, 'hours').format('DD/MM/YYYY HH:mm:ss')} - ${moment(endDate).add(3, 'hours').format('DD/MM/YYYY HH:mm:ss')}`
            : '-';
        const sürücüAdıSoyadı = infoData?.driverName && infoData?.driverSurName
            ? `${infoData.driverName} ${infoData.driverSurName}`
            : 'Tanimlanmadi';
        const raporOluşturulmaTarihi = moment().format('DD.MM.YYYY HH:mm');
        const raporuOluşturan = user?.name || '-';

        // Bilgileri PDF üst kısmına ekleme
        doc.setFontSize(12);
        doc.text(`Kapi Numarasi: ${kapıNumarası}`, 15, 10);
        doc.text(`Rapor Tarih Araliklari: ${raporTarihAralıkları}`, 120, 10);
        doc.text(`Arac Plakasi: ${araçPlakası}`, 15, 15);
        doc.text(`Surucu Adi / Soyadi: ${sürücüAdıSoyadı}`, 120, 15);
        doc.text(`Sasi Numarasi: ${chassisNumber}`, 15, 20);
        doc.text(`Hat Numarasi : Tanimlanmadi`, 120, 20);
        doc.text(`Rapor Olusturulma Tarihi: ${raporOluşturulmaTarihi}`, 15, 25);
        doc.text(`Raporu Olusturan: ${raporuOluşturan}`, 120, 25);
        doc.text(`Toplam KM: ${toplamKM}`, 15, 30);

        // GPS formatlayıcı
        const formatGps = (gps) => {
            return gps ? gps : 'Tanimlanmadi';
        };

        // PDF'e Tablo Eklenmesi
        doc.autoTable({
            head: [['Olusturulma Tarih/Saat', 'Kapi Numarasi', 'Plaka', 'Sasi Numarasi', 'Baslangic Tarihi', 'Bitis Tarihi', 'Sure', 'Motor Hizi [RPM]', 'Arac Hizi [km/h]', 'Konum']],
            body: tableData.map((item) => [
                (() => {
                    const date = new Date(item.createdAt);
                    date.setHours(date.getHours() + 3);
                    return date.toISOString().slice(0, 19).replace('T', ' ');
                })(),
                item.operationNumber || '-',
                item.plateNumber || '-',
                item.chassisNumber || '-',
                item.dates?.start
                    ? moment.utc(item.dates.start).add(3, 'hours').format('DD/MM/YYYY HH:mm:ss')
                    : '-',
                item.dates?.end
                    ? moment.utc(item.dates.end).add(3, 'hours').format('DD/MM/YYYY HH:mm:ss')
                    : '-',
                item.duration || '-',
                item.engineSpeed || '-',
                item.wheelBasedSpeed || '-',
                formatGps(item.gps),
            ]),
            startY: 40,
        });
        doc.save('Kontak_Mesafe_Raporu.pdf');
    };

    const handleExcel = () => {

        const sanitizedOperationNumber = infoData?.operationNumber?.replace(/\n/g, '') || '-';
        const plateNumber = infoData?.plateNumber || '-';
        const chassisNumber = infoData?.chassisNumber || '-';
        const start_date = startDate ? moment.utc(startDate).add(3, 'hours').format('DD/MM/YYYY HH:mm:ss') : 'Tanımlanmadı';
        const end_date = endDate ? moment.utc(endDate).add(3, 'hours').format('DD/MM/YYYY HH:mm:ss') : 'Tanımlanmadı';
        const reportCreationDate = moment().format('DD/MM/YYYY HH:mm:ss');
        const driver = infoData?.driverName && infoData?.driverSurName ? `${infoData.driverName} ${infoData.driverSurName}` : 'Tanımlanmadı';

        const formatGps = (gps) => {
            return gps ? gps : '-';
        };

        const processedData = tableData.map((item) => {
            const createdAtDate = new Date(item.createdAt);
            createdAtDate.setHours(createdAtDate.getHours() + 3);
            const adjustedDate = createdAtDate.toISOString().slice(0, 19).replace('T', ' ');

            return {
                'Oluşturulma Tarihi': adjustedDate,
                'Kapı Numarası': item.operationNumber || '-',
                'Plaka': item.plateNumber || '-',
                'Şasi Numarası': item.chassisNumber || '-',
                'Başlangıç Tarihi': moment.utc(item.dates?.start).add(3, 'hours').format('DD/MM/YYYY HH:mm:ss') || '-',
                'Bitiş Tarihi': moment.utc(item.dates?.end).add(3, 'hours').format('DD/MM/YYYY HH:mm:ss') || '-',
                'Süre': item.duration || '-',
                'Motor Hızı [RPM]': item.engineSpeed || '-',
                'Araç Hızı [km/h]': item.wheelBasedSpeed || '-',
                'Konum': formatGps(item.gps),
            };
        });

        const worksheet = XLSX.utils.json_to_sheet([]);

        XLSX.utils.sheet_add_aoa(worksheet, [
            ['Kapı Numarası:', sanitizedOperationNumber, '', 'Rapor Tarih Aralıkları:', `${start_date} - ${end_date}`],
            ['Araç Plakası:', plateNumber, '', 'Sürücü Adı / Soyadı:', driver],
            ['Şase Numarası:', chassisNumber, '', 'Hat Numarası:', 'Tanımlanmadı'],
            ['Rapor Oluşturulma Tarihi:', reportCreationDate, '', 'Raporu oluşturan:', user?.name],
            ['Toplam KM :', infoData.totalDistance],
        ], {origin: 'A1'});

        // Add processed data starting at row 6
        XLSX.utils.sheet_add_json(worksheet, processedData, {origin: 'A7', skipHeader: false});

        // Set column widths dynamically based on content length
        const maxLengths = processedData.reduce((acc, curr) => {
            Object.keys(curr).forEach((key, index) => {
                const length = curr[key] ? curr[key].toString().length : 15;
                acc[index] = Math.max(acc[index] || 15, length);
            });
            return acc;
        }, []);

        worksheet['!cols'] = maxLengths.map(len => ({wch: len + 1}));

        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Kontak Mesafe Raporu');

        XLSX.writeFile(workbook, 'Kontak_Mesafe_Raporu.xlsx');
    };

    return (
        <Row justify="center">
            <Title title="Octoopi Cocpit | Kontak/Mesafe Raporu" description="Mertech Arge A.Ş Kontak/Mesafe Raporu"/>
            <Col xs={24} sm={24} md={24} lg={24} xl={16} xxl={12}>
                <Card title="Kontak/Mesafe Raporu" type="inner" size="small" style={{marginBottom: 10}}>
                    <SelectVehicle name="vehicleID" onChange={(e) => setVehicleID(e)}/>
                    <Form.Item name="byDate" label="Tarih Aralığı">
                        <RangePicker
                            showTime disabled={isLoading}
                            disabledDate={disabledDate}
                            placeholder={['Başlangıç Tarihi', 'Bitiş Tarihi']}
                            style={{margin: "auto", marginRight: 3, width: "100%"}}
                            onChange={handleDateChange}
                        />
                    </Form.Item>
                    <Button
                        type="primary"
                        icon={<DownloadOutlined/>}
                        onClick={handleQuery}
                        style={{marginTop: '16px'}}
                        loading={isLoading}
                        disabled={!vehicle_id}
                    >
                        Rapor Oluştur
                    </Button>
                </Card>
            </Col>
            <Col xs={24} sm={24} md={24} lg={24} xl={20} xxl={24}>
                <Card
                    type="inner"
                    title="Sonuçlar"
                    size="small"
                    extra={
                        <>
                            <Button
                                style={{marginRight: 5}}
                                size="small"
                                disabled={!tableData.length}
                                type="primary"
                                icon={<FileTextOutlined/>}
                                onClick={handleExcel}
                            >Excel</Button>
                            <Button
                                size="small"
                                disabled={!tableData.length}
                                type="primary"
                                icon={<FilePdfOutlined/>}
                                onClick={generatePDF}
                            >Pdf</Button>
                        </>
                    }
                >
                    <Table
                        rowKey="id"
                        loading={isLoading}
                        bordered={true}
                        size="small"
                        columns={columns}
                        dataSource={tableData}
                        error={error}
                        pagination={{
                            current: pagination.current,
                            pageSize: pagination.pageSize,
                            total: pagination.total,
                            onChange: onPageChange,
                        }}
                    />
                </Card>
            </Col>
        </Row>
    );

};

export default ContactReport;
